import React, { useState, useEffect } from 'react';
import { toOutputSchemaString, normalizeTemplate } from './utils';
import apiService from '../../../../services/api';

const EditModal = ({
  show,
  template,
  error,
  onCancel,
  onError,
  onSuccess,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    version: "1",
    content: "",
    outputSchema: "",
    isActive: false,
  });
  const [saving, setSaving] = useState(false);

  // Initialize formData when modal opens or template changes
  useEffect(() => {
    if (show && template) {
      setFormData({
        name: template.name || "",
        version: template.version?.toString() || "1",
        content: template.content || "",
        outputSchema: toOutputSchemaString(
          template.rawOutputSchema ?? template.outputSchema
        ),
        isActive: Boolean(template.isActive),
      });
      // Clear errors when modal opens
      if (onError) {
        onError(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, template]);

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear errors when user starts typing
    if (onError && error) {
      onError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!template) {
      return;
    }

    const trimmedName = formData.name.trim();
    const trimmedContent = formData.content.trim();
    const trimmedVersion = formData.version?.toString().trim();
    const trimmedOutputSchema = formData.outputSchema?.trim();

    if (!trimmedName) {
      if (onError) {
        onError("Template name is required");
      }
      return;
    }

    if (!trimmedVersion) {
      if (onError) {
        onError("Version is required");
      }
      return;
    }

    const versionValue = Number(trimmedVersion);
    if (!Number.isInteger(versionValue) || versionValue < 1) {
      if (onError) {
        onError("Version must be a positive integer");
      }
      return;
    }

    if (!trimmedContent) {
      if (onError) {
        onError("Content is required");
      }
      return;
    }

    let outputSchemaPayload = null;
    if (trimmedOutputSchema) {
      try {
        outputSchemaPayload = JSON.parse(trimmedOutputSchema);
      } catch (err) {
        console.error("Invalid output schema JSON:", err);
        if (onError) {
          onError("Output schema must be valid JSON");
        }
        return;
      }
    }

    const payload = {
      name: trimmedName,
      version: versionValue,
      content: trimmedContent,
      outputSchema: outputSchemaPayload,
      isActive: formData.isActive,
      promptKey: template.promptKey || null,
      category: template.category || 'custom',
    };

    try {
      setSaving(true);
      if (onError) {
        onError(null);
      }

      const response = await apiService.updatePromptTemplate(
        template.id,
        payload
      );

      if (response?.success) {
        const normalized = normalizeTemplate(response.data, template.id);
        if (normalized && onSuccess) {
          onSuccess(normalized);
        }
        if (onClose) {
          onClose();
        }
      } else {
        if (onError) {
          onError(response?.message || "Failed to update prompt template");
        }
      }
    } catch (err) {
      console.error("Error updating prompt template:", err);
      if (onError) {
        onError(
          err.response?.data?.message || "Failed to update prompt template"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (template) {
      setFormData({
        name: template.name || "",
        version: template.version?.toString() || "1",
        content: template.content || "",
        outputSchema: toOutputSchemaString(
          template.rawOutputSchema ?? template.outputSchema
        ),
        isActive: Boolean(template.isActive),
      });
    }
    onCancel();
  };

  if (!show) {
    return null;
  }

  const isSystem = template?.category === 'system';

  return (
    <div
      className="prompt-template-detail__modal-overlay"
      onClick={handleCancel}
    >
      <div
        className="prompt-template-detail__modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="prompt-template-detail__modal-header">
          <h2 className="prompt-template-detail__modal-title">
            {isSystem ? 'Edit System Prompt' : 'Edit Template'}
          </h2>
          <button
            type="button"
            className="prompt-template-detail__modal-close"
            onClick={handleCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="prompt-template-detail__modal-body">
            {error && (
              <div className="prompt-template-detail__alert prompt-template-detail__alert--error">
                {error}
              </div>
            )}

            {isSystem && template.promptKey && (
              <div className="prompt-template-detail__form-group">
                <label>Prompt Key</label>
                <code style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(139, 92, 246, 0.08)',
                  color: '#7c3aed',
                  fontSize: '13px',
                  fontFamily: "'Courier New', monospace",
                  border: '1px solid rgba(139, 92, 246, 0.15)',
                }}>
                  {template.promptKey}
                </code>
              </div>
            )}

            <div className="prompt-template-detail__form-group">
              <label htmlFor="template-name">Template Name *</label>
              <input
                id="template-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter template name"
                required
              />
            </div>

            <div className="prompt-template-detail__form-group">
              <label htmlFor="template-version">Version *</label>
              <input
                id="template-version"
                name="version"
                type="number"
                min={1}
                value={formData.version}
                onChange={handleChange}
                required
              />
            </div>

            <div className="prompt-template-detail__form-group prompt-template-detail__form-group--checkbox">
              <label htmlFor="template-active">
                <input
                  id="template-active"
                  name="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Mark as active</span>
              </label>
            </div>

            <div className="prompt-template-detail__form-group">
              <label htmlFor="template-content">Content *</label>
              <textarea
                id="template-content"
                name="content"
                rows={8}
                value={formData.content}
                onChange={handleChange}
                placeholder="Provide the prompt instructions to execute."
                required
              />
            </div>

            <div className="prompt-template-detail__form-group">
              <label htmlFor="template-output-schema">Output Schema (JSON)</label>
              <textarea
                id="template-output-schema"
                name="outputSchema"
                rows={8}
                value={formData.outputSchema}
                onChange={handleChange}
                placeholder='{"fields": []}'
              />
            </div>
          </div>

          <div className="prompt-template-detail__modal-footer">
            <button
              type="button"
              className="user-home-layout__action-button user-home-layout__action-button--secondary prompt-template-detail__action-button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="user-home-layout__action-button user-home-layout__action-button--primary prompt-template-detail__action-button"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;

